/**
 * Gems Mine — the artwork. Every element hand-drawn SVG; no external assets.
 *
 * THE THREE LESSONS THIS FILE IS BUILT ON (paid for on the roulette wheel):
 *
 *   1. FACETS, NOT GRADIENTS. A gem reads as 3-D when ADJACENT facets take
 *      hard-different amounts of light; a smooth ramp reads as a sticker.
 *      Every gem is a crown of flat polygons with deliberate tonal jumps.
 *   2. METAL IS BANDED. Polished metal reflects a bright ceiling and a dark
 *      room meeting at a horizon — light/dark/light bands. Smooth silver is
 *      grey plastic every time.
 *   3. NO `filter=` ANYWHERE (Rule Zero). Glow is stacked opacity shapes,
 *      shadow is a flat ellipse. SVG filters are the slowest paint path a
 *      phone has.
 *
 * Motion lives in gems.css; this file provides bones + class hooks.
 */

import type { GemKind } from './engine';
import { CUT_SVGS, BOMB_SVG, LANTERN_SVG, BEAM_H_SVG, PROP_V_SVG, VEIN_SVG } from './cuts';

/* ─── shared paint servers, mounted once by the Page ─────────────────────── */

export function ArtDefs() {
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden>
      <defs>
        <radialGradient id="gm-lamp" cx="50%" cy="45%" r="55%">
          <stop offset="0%" stopColor="#FFD98A" stopOpacity=".95" />
          <stop offset="45%" stopColor="#F2A93C" stopOpacity=".5" />
          <stop offset="100%" stopColor="#8A4E10" stopOpacity="0" />
        </radialGradient>
        {/* cast iron — BANDED */}
        <linearGradient id="gm-iron" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#5A6470" />
          <stop offset="22%" stopColor="#1E242B" />
          <stop offset="46%" stopColor="#454F5A" />
          <stop offset="70%" stopColor="#10151A" />
          <stop offset="100%" stopColor="#2A323B" />
        </linearGradient>
        {/* pickaxe steel — BANDED */}
        <linearGradient id="gm-steel" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#EAF2F8" />
          <stop offset="30%" stopColor="#9FB0BE" />
          <stop offset="52%" stopColor="#F4FAFF" />
          <stop offset="75%" stopColor="#5E6E7C" />
          <stop offset="100%" stopColor="#8A9AA8" />
        </linearGradient>
        <linearGradient id="gm-wood" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#8A5B2B" />
          <stop offset="45%" stopColor="#B07A3C" />
          <stop offset="60%" stopColor="#7A4E22" />
          <stop offset="100%" stopColor="#5E3A17" />
        </linearGradient>
        <linearGradient id="gm-rock" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#5E5648" />
          <stop offset="55%" stopColor="#443E33" />
          <stop offset="100%" stopColor="#2C2820" />
        </linearGradient>
        <linearGradient id="gm-rock-lit" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#776C59" />
          <stop offset="100%" stopColor="#3A342A" />
        </linearGradient>
        <radialGradient id="gm-socket" cx="50%" cy="42%" r="65%">
          <stop offset="0%" stopColor="#2A2318" />
          <stop offset="70%" stopColor="#140F0A" />
          <stop offset="100%" stopColor="#070503" />
        </radialGradient>
      </defs>
    </svg>
  );
}

export function Gem({ kind, size = 64, idle = true, reveal = false }: {
  kind: GemKind; size?: number | string; idle?: boolean; reveal?: boolean;
}) {
  /* Each kind is a UNIQUE cut (step / cushion / marquise / pear / brilliant)
     from the cut library — silhouette alone names the stone. Raw string via
     innerHTML keeps the designed SVG byte-exact; CSS animates .cut-glint /
     .cut-spark inside it. */
  return (
    <span className={`gm-gem gm-gem--${kind} ${idle ? 'gm-gem--idle' : ''} ${reveal ? 'gm-gem--reveal' : ''}`}
          style={{ width: size, height: size }} aria-hidden
          dangerouslySetInnerHTML={{ __html: CUT_SVGS[kind] }} />
  );
}

/* ─── the bomb ───────────────────────────────────────────────────────────
   A cast-iron sphere. The banding does the realism; the animated spark on the
   fuse does the menace. viewBox 0 0 100 100. */

export function Bomb({ size = 64, lit = true }: { size?: number | string; lit?: boolean }) {
  /* the kettlebomb from the cut library — glossy banded iron, brass collar,
     braided fuse; .bomb-spark flickers only while lit (CSS hides it otherwise) */
  return (
    <span className={`gm-bomb ${lit ? 'gm-bomb--lit' : ''}`}
          style={{ width: size, height: size }} aria-hidden
          dangerouslySetInnerHTML={{ __html: BOMB_SVG }} />
  );
}

/* ─── the pickaxe ────────────────────────────────────────────────────────
   Held at 45°, head up-right, ready to strike down-left — the swing animation
   in gems.css rotates the whole svg about the handle's butt. viewBox 0 0 120 120. */

export function Pickaxe({ size = 96 }: { size?: number | string }) {
  return (
    <svg viewBox="0 0 120 120" width={size} height={size} className="gm-pick" aria-hidden>
      {/* handle: hickory, with the grain drawn on */}
      <g transform="rotate(45 60 60)">
        <rect x="54" y="26" width="12" height="86" rx="5" fill="url(#gm-wood)" stroke="#3E2610" strokeWidth="1.6" />
        <path d="M58,32 C56,52 62,72 58,104" stroke="#5E3A17" strokeWidth="1.1" fill="none" opacity=".7" />
        <path d="M63,30 C65,54 60,80 64,106" stroke="#8A5B2B" strokeWidth="1" fill="none" opacity=".6" />
        {/* butt cap + collar */}
        <rect x="52.6" y="106" width="14.8" height="6.4" rx="3" fill="#3E2610" />
        <rect x="52" y="26" width="16" height="7" rx="2.4" fill="url(#gm-iron)" stroke="#0B0F13" strokeWidth="1.2" />
        {/* the head: two curved picks, one continuous steel crescent */}
        <path d="M12,38 C34,14 86,14 108,38 C88,28 74,24 60,24 C46,24 32,28 12,38 Z"
              fill="url(#gm-steel)" stroke="#2E3A44" strokeWidth="1.8" strokeLinejoin="round" />
        {/* the crescent's own hard reflection band */}
        <path d="M22,33 C42,20 78,20 98,33" fill="none" stroke="#FFFFFF" strokeWidth="2" opacity=".75" strokeLinecap="round" />
        {/* points, sharpened: a darker underside plane on each tip */}
        <path d="M12,38 C20,33 27,30 34,28 L30,34 Z" fill="#5E6E7C" />
        <path d="M108,38 C100,33 93,30 86,28 L90,34 Z" fill="#5E6E7C" />
      </g>
    </svg>
  );
}

/* ─── the tiles ──────────────────────────────────────────────────────────
   A rock slab. Five shape variants so the wall never tiles visibly; the same
   seed always returns the same slab, so re-renders cannot reshuffle the mine.
   viewBox 0 0 100 100. */

const SLABS = [
  'M8,12 L52,5 L93,10 L96,55 L90,92 L46,96 L10,90 L4,48 Z',
  'M6,10 L48,7 L94,13 L95,50 L92,94 L52,92 L8,95 L5,52 Z',
  'M10,8 L55,6 L92,12 L97,46 L93,90 L50,95 L6,88 L7,44 Z',
  'M7,14 L50,4 L95,9 L94,52 L96,90 L48,93 L9,94 L3,50 Z',
  'M9,9 L46,8 L96,11 L93,48 L95,93 L54,90 L7,92 L6,46 Z',
];
const CRACKS = [
  'M22,20 L34,38 L28,52 M70,70 L60,58',
  'M78,24 L64,40 L70,55 M24,78 L38,66',
  'M30,80 L42,62 L36,50 M72,26 L62,40',
  'M20,50 L38,44 L46,56 M74,74 L64,64',
  'M76,48 L60,54 L52,42 M28,26 L40,38',
];

export function TileFace({ seed, lit = false }: { seed: number; lit?: boolean }) {
  const v = ((seed % 5) + 5) % 5;
  return (
    <svg viewBox="0 0 100 100" className="gm-tileface" aria-hidden preserveAspectRatio="none">
      <path d={SLABS[v]} fill={lit ? 'url(#gm-rock-lit)' : 'url(#gm-rock)'} />
      {/* bevel: lit top-left edge, dark bottom-right — the slab sits PROUD */}
      <path d={SLABS[v]} fill="none" stroke="#8A7E68" strokeWidth="2.6" opacity=".55"
            strokeDasharray="90 400" strokeDashoffset="-8" />
      <path d={SLABS[v]} fill="none" stroke="#14100B" strokeWidth="3" opacity=".65"
            strokeDasharray="120 400" strokeDashoffset="-215" />
      {/* hairline cracks + mineral speckles */}
      <path d={CRACKS[v]} fill="none" stroke="#1E1912" strokeWidth="1.6" opacity=".6" strokeLinecap="round" />
      <circle cx={20 + (seed * 13) % 60} cy={30 + (seed * 29) % 40} r="1.6" fill="#8A7E68" opacity=".5" />
      <circle cx={30 + (seed * 7) % 50} cy={20 + (seed * 17) % 55} r="1.1" fill="#A89A7E" opacity=".4" />
      {/* the idle shimmer bar the CSS sweeps */}
      <g className="gm-tilesheen">
        <path d="M-20,110 L20,-10 L34,-10 L-6,110 Z" fill="#FFF6D8" opacity=".08" />
      </g>
    </svg>
  );
}

/** The dark socket behind a broken tile. */
export function Socket() {
  return (
    <svg viewBox="0 0 100 100" className="gm-socketface" aria-hidden preserveAspectRatio="none">
      <rect x="2" y="2" width="96" height="96" rx="10" fill="url(#gm-socket)" />
      <rect x="2" y="2" width="96" height="96" rx="10" fill="none" stroke="#000" strokeWidth="3" opacity=".55" />
      <path d="M10,86 Q50,96 90,86 L90,94 Q50,102 10,94 Z" fill="#000" opacity=".4" />
    </svg>
  );
}

/** The heavy crack that flashes the instant a strike lands. */
export function CrackBurst() {
  return (
    <svg viewBox="0 0 100 100" className="gm-crackburst" aria-hidden preserveAspectRatio="none">
      <g stroke="#0E0B07" strokeWidth="2.6" fill="none" strokeLinecap="round">
        <path d="M50,50 L18,22 M50,50 L82,18 M50,50 L88,62 M50,50 L60,88 M50,50 L20,78 M50,50 L8,52" />
      </g>
      <g stroke="#FFF6D8" strokeWidth="1" fill="none" opacity=".5" strokeLinecap="round">
        <path d="M50,50 L22,26 M50,50 L78,22 M50,50 L84,60 M50,50 L58,84" />
      </g>
    </svg>
  );
}

/** Six flying shards for the shatter — each carries its own CSS trajectory. */
export function Shards() {
  const bits = [
    'M0,0 L14,4 L8,16 Z', 'M0,0 L12,-6 L18,6 Z', 'M0,0 L-12,6 L-6,16 Z',
    'M0,0 L-14,-4 L-4,-14 Z', 'M0,0 L10,10 L-2,14 Z', 'M0,0 L6,-14 L14,-6 Z',
  ];
  return (
    <svg viewBox="-50 -50 100 100" className="gm-shards" aria-hidden>
      {bits.map((d, i) => (
        <g key={i} className={`gm-shard gm-shard--${i}`}>
          <path d={d} fill="#443E33" stroke="#14100B" strokeWidth="1" />
        </g>
      ))}
    </svg>
  );
}

/* ─── the reveal rays — the light that bursts when a gem is freed ────────── */

export function RaysBurst({ tone }: { tone: string }) {
  return (
    <svg viewBox="-60 -60 120 120" className="gm-rays" aria-hidden>
      {Array.from({ length: 8 }, (_, i) => (
        <path key={i} d="M0,-4 L52,-1.4 L52,1.4 L0,4 Z" fill={tone} opacity=".55"
              transform={`rotate(${i * 45})`} />
      ))}
      <circle r="14" fill="#FFFFFF" opacity=".8" />
    </svg>
  );
}

/* ─── the explosion — flash, ring, sparks, smoke. One shot, CSS-run. ─────── */

export function Explosion() {
  return (
    <svg viewBox="-60 -60 120 120" className="gm-boom" aria-hidden>
      <circle className="gm-boom-flash" r="26" fill="#FFF3C4" />
      <circle className="gm-boom-ring" r="18" fill="none" stroke="#FFB53C" strokeWidth="5" />
      {Array.from({ length: 8 }, (_, i) => (
        <path key={i} className={`gm-boom-spark gm-boom-spark--${i}`}
              d="M0,0 L3,-16 L-3,-16 Z" fill={i % 2 ? '#FFD200' : '#FF7A29'}
              transform={`rotate(${i * 45})`} />
      ))}
      <circle className="gm-boom-smoke gm-boom-smoke--a" cx="-12" cy="-6" r="12" fill="#3A3A3A" opacity=".5" />
      <circle className="gm-boom-smoke gm-boom-smoke--b" cx="10" cy="-12" r="15" fill="#2A2A2A" opacity=".5" />
      <circle className="gm-boom-smoke gm-boom-smoke--c" cx="0" cy="8" r="11" fill="#454545" opacity=".5" />
    </svg>
  );
}

/* ─── the hero lantern — the mine's heartbeat, big enough to own its corner ── */

export function Lantern({ size = 64, flip = false }: { size?: number | string; flip?: boolean }) {
  return (
    <span className={`gm-lantern ${flip ? 'gm-lantern--flip' : ''}`}
          style={{ width: size, aspectRatio: '100 / 140' }} aria-hidden
          dangerouslySetInnerHTML={{ __html: LANTERN_SVG }} />
  );
}

/* ─── the timber portal — beams + props that frame the wall like a shaft ──── */

export function TimberBeam() {
  return <span className="gm-beam" aria-hidden dangerouslySetInnerHTML={{ __html: BEAM_H_SVG }} />;
}
export function TimberProp() {
  return <span className="gm-prop" aria-hidden dangerouslySetInnerHTML={{ __html: PROP_V_SVG }} />;
}

/* ─── gold veins — shown on ~40% of slabs so the wall promises treasure ───── */

export function VeinOverlay() {
  return <span className="gm-vein" aria-hidden dangerouslySetInnerHTML={{ __html: VEIN_SVG }} />;
}
